/**
 * Ray fan configuration for the hero convergence graphic.
 *
 * BRAND-SPEC.md "Hero graphic" is explicit that the client expects to tune ray
 * count, angles and timing during implementation, so the fan is generated from
 * this array rather than written as markup. Tuning the graphic is an edit to
 * this file and nothing else.
 *
 * Every value except `tier` is transcribed verbatim from the design reference
 * docs/revamp-july-2026/handoff/Focal Point Site.dc.html lines 48 to 58, in
 * source order. `tier` is new: the reference is desktop-only and eleven
 * simultaneously animated blurred elements is more paint than a mid-range phone
 * should be asked for, so each ray declares the smallest breakpoint at which it
 * renders.
 *
 * Determinism matters here. Every duration and delay is a literal. Never
 * generate them with Math.random() or Date.now(): the hero is server rendered
 * and a randomised fan would produce a different DOM on the server and the
 * client (docs/product-knowledge/technical/coding-standards.md, Hydration
 * Safety).
 */

/**
 * Smallest breakpoint tier at which a ray renders.
 *
 * 'base' carries no gate of its own. The others gate on a min-width media
 * query: 'sm' 640px, 'md' 768px, 'xl' 1280px.
 *
 * Since #7943 the field itself is hidden below 768px, so 'base', 'sm' and 'md'
 * all first paint together at 768. The gates are still written per tier rather
 * than collapsed to two, so this field keeps its literal meaning.
 */
export type FpTier = 'base' | 'sm' | 'md' | 'xl';

export interface Ray {
  /** Degrees. Negative rotates the ray up, positive down. Pivot is the convergence point. */
  angle: number;
  /** Length in px at the desktop scale. Multiplied by --fp-scale at smaller breakpoints. */
  length: number;
  /** Peak alpha of the ink blue band, 0 to 1. */
  alpha: number;
  /** Blur radius in px at the desktop scale. 0 on the centre ray. */
  blur: number;
  /** Travelling band width as a percentage of the ray's length. */
  band: number;
  /** Which keyframe track: 'a' is fpFlowA, 'b' is fpFlowB. */
  flow: 'a' | 'b';
  /** Animation duration, seconds. */
  duration: number;
  /** Animation delay, seconds. Must stay a literal; never randomise (SSR determinism). */
  delay: number;
  /** CSS timing function. */
  easing: string;
  /** Smallest breakpoint tier at which this ray renders. */
  tier: FpTier;
}

/**
 * The eleven rays, outermost negative angle first.
 *
 * The tier assignment produces an angularly symmetric fan at every step, so no
 * tier ever looks like a fan with teeth missing:
 *
 *   base (0px)     3 rays   -28, 0, +28
 *   sm   (640px)   5 rays   -46, -28, 0, +28, +46
 *   md   (768px)   7 rays   -46, -28, -12, 0, +12, +28, +46
 *   xl   (1280px) 11 rays   all
 *
 * Base is the +/-28 pair rather than the widest one on purpose: +/-28 carries
 * 2.10px of blur against +/-46's 3.45px, so three rays read as three rays
 * instead of two smudges and a line.
 *
 * Two of those four steps are currently theoretical. #7943 hid the whole field
 * below 768px, so what a reader actually sees is the md step at 768 and the xl
 * step at 1280. The base and sm rows are kept because they are what makes the
 * ladder symmetric and they are the answer if the field ever earns its space on
 * a phone again.
 */
export const RAY_FAN: readonly Ray[] = [
  { angle: -46, length: 640, alpha: 0.39, blur: 3.45, band: 58, flow: 'a', duration: 8.9, delay: 1.29, easing: 'cubic-bezier(.35,.05,.25,1)', tier: 'sm' },
  { angle: -37, length: 662, alpha: 0.45, blur: 2.78, band: 63, flow: 'b', duration: 8.7, delay: 1.26, easing: 'cubic-bezier(.5,.1,.4,.95)', tier: 'xl' },
  { angle: -28, length: 684, alpha: 0.52, blur: 2.1, band: 68, flow: 'a', duration: 8.6, delay: 1.44, easing: 'ease-in-out', tier: 'base' },
  { angle: -20, length: 640, alpha: 0.58, blur: 1.5, band: 73, flow: 'b', duration: 7.5, delay: 1.22, easing: 'cubic-bezier(.35,.05,.25,1)', tier: 'xl' },
  { angle: -12, length: 662, alpha: 0.63, blur: 0.9, band: 58, flow: 'a', duration: 7.4, delay: 0.34, easing: 'cubic-bezier(.5,.1,.4,.95)', tier: 'md' },
  { angle: 0, length: 684, alpha: 0.72, blur: 0, band: 63, flow: 'b', duration: 6.5, delay: 0.22, easing: 'ease-in-out', tier: 'base' },
  { angle: 12, length: 640, alpha: 0.63, blur: 0.9, band: 68, flow: 'a', duration: 6.4, delay: 0.78, easing: 'cubic-bezier(.35,.05,.25,1)', tier: 'md' },
  { angle: 20, length: 662, alpha: 0.58, blur: 1.5, band: 73, flow: 'b', duration: 7.5, delay: 1.22, easing: 'cubic-bezier(.5,.1,.4,.95)', tier: 'xl' },
  { angle: 28, length: 684, alpha: 0.52, blur: 2.1, band: 58, flow: 'a', duration: 8.6, delay: 0.78, easing: 'ease-in-out', tier: 'base' },
  { angle: 37, length: 640, alpha: 0.45, blur: 2.78, band: 63, flow: 'b', duration: 8.7, delay: 1.26, easing: 'cubic-bezier(.35,.05,.25,1)', tier: 'xl' },
  { angle: 46, length: 662, alpha: 0.39, blur: 3.45, band: 68, flow: 'a', duration: 8.9, delay: 1.73, easing: 'cubic-bezier(.5,.1,.4,.95)', tier: 'sm' },
];
